import { IAddress } from '../../indigofair/data/IAddress';
import { IBrandOrder } from '../../indigofair/data/IBrandOrder';
import { ICurrencyConversionRate } from '../../indigofair/data/ICurrencyConversionRate';
import { IFaireMoney } from '../../indigofair/data/IFaireMoney';
import { IFilterSection } from '../../indigofair/data/IFilterSection';
import { IImage } from '../../indigofair/data/IImage';
import { IOrderItem } from '../../indigofair/data/IOrderItem';
import { IPaginationData } from '../../indigofair/data/IPaginationData';
import { IRetailerStoreDataForOrders } from '../../indigofair/data/IRetailerStoreDataForOrders';
import { IShipWindow } from '../../indigofair/data/IShipWindow';
import { OrderItemIssueAction } from '../../indigofair/data/OrderItemIssueAction';
import { PaymentTerm } from '../../indigofair/data/PaymentTerm';

export namespace IListOrdersByBrandOrderResponse {
  export type RetailerIdvStatusType =
    | 'RETAILER_IDV_STATUS_UNKNOWN'
    | 'NEVER_RECEIVED_IDV'
    | 'HAS_PENDING_IDV_REQUEST'
    | 'IDV_REQUEST_RESPONDED';

  export enum RetailerIdvStatus {
    /**
     * Unknown status.
     */
    RETAILER_IDV_STATUS_UNKNOWN = 'RETAILER_IDV_STATUS_UNKNOWN',
    /**
     * The retailer has never received any IDV request.
     */
    NEVER_RECEIVED_IDV = 'NEVER_RECEIVED_IDV',
    /**
     * There is an pending IDV request waiting for the retailer's response.
     */
    HAS_PENDING_IDV_REQUEST = 'HAS_PENDING_IDV_REQUEST',
    /**
     * The retailer has responded all IDV requests.
     */
    IDV_REQUEST_RESPONDED = 'IDV_REQUEST_RESPONDED',
  }

  export namespace IBrandOrderData {
    export namespace IBrandData {
      /**
       * Return an instance of IBrandData with the provided fields. Any field not
       * listed will be populated with an empty value (an empty array,
       * or undefined, whichever is valid for the type of the value.)
       */
      export const build = (partial?: Partial<IBrandData>): IBrandData =>
        ({
          ...(partial ?? {}),
        } as IBrandData);
    }

    /**
     * Bundle of data about the brand
     */
    export interface IBrandData {
      token: string | undefined;
      name: string | undefined;
      active: boolean | undefined;
      /**
       * Whether this brand is owned by Faire. This means it's the Mix and Match or Sales Collection brands.
       */
      is_internal: boolean | undefined;
    }

    export namespace IItemData {
      /**
       * Return an instance of IItemData with the provided fields. Any field not
       * listed will be populated with an empty value (an empty array,
       * or undefined, whichever is valid for the type of the value.)
       */
      export const build = (partial?: Partial<IItemData>): IItemData =>
        ({
          ...(partial ?? {}),
        } as IItemData);
    }

    /**
     * Bundle of data about an individual item within the order
     */
    export interface IItemData {
      product_token: string | undefined;
      product_name: string | undefined;
      state: keyof typeof IOrderItem.State | undefined;
      is_returnable: boolean | undefined;
      /**
       * Could be either the product option image or the product image.
       */
      image: IImage | undefined;
      quantity: number | undefined;
      issue_action: keyof typeof OrderItemIssueAction | undefined;
    }

    export namespace IBrandReviewData {
      /**
       * Return an instance of IBrandReviewData with the provided fields. Any field not
       * listed will be populated with an empty value (an empty array,
       * or undefined, whichever is valid for the type of the value.)
       */
      export const build = (
        partial?: Partial<IBrandReviewData>
      ): IBrandReviewData =>
        ({
          ...(partial ?? {}),
        } as IBrandReviewData);
    }

    /**
     * The other states don't need "at" dates because we don't show that information
     * in the UI. For example we just show "Processing", not "Processing at XYZ"
     * Bundle of data about brand review
     */
    export interface IBrandReviewData {
      token: string | undefined;
      rating: number | undefined;
    }

    export namespace IEligibility {
      export type EnumType =
        | 'ENUM_UNKNOWN'
        | 'NOT_ELIGIBLE'
        | 'ELIGIBLE'
        | 'MANUAL_PROCESS_REQUIRED';

      export enum Enum {
        /**
         * Should not be used.
         * TODO(noah): change this to UNKNOWN once Faire/backend#58586 lands before we launch
         */
        ENUM_UNKNOWN = 'ENUM_UNKNOWN',
        /**
         * Not eligible for this action
         */
        NOT_ELIGIBLE = 'NOT_ELIGIBLE',
        /**
         * Eligible for this action without any issues
         */
        ELIGIBLE = 'ELIGIBLE',
        /**
         * Requires someone to review and process the eligibility of this action
         */
        MANUAL_PROCESS_REQUIRED = 'MANUAL_PROCESS_REQUIRED',
      }

      /**
       * Return an instance of IEligibility with the provided fields. Any field not
       * listed will be populated with an empty value (an empty array,
       * or undefined, whichever is valid for the type of the value.)
       */
      export const build = (partial?: Partial<IEligibility>): IEligibility =>
        ({
          ...(partial ?? {}),
        } as IEligibility);
    }

    export interface IEligibility {}
    export namespace IShipmentData {
      export namespace IShipmentStatus {
        /**
         * Return an instance of IShipmentStatus with the provided fields. Any field not
         * listed will be populated with an empty value (an empty array,
         * or undefined, whichever is valid for the type of the value.)
         */
        export const build = (
          partial?: Partial<IShipmentStatus>
        ): IShipmentStatus =>
          ({
            ...(partial ?? {}),
          } as IShipmentStatus);
      }

      export interface IShipmentStatus {
        description: string | undefined;
        color: string | undefined;
      }

      /**
       * Return an instance of IShipmentData with the provided fields. Any field not
       * listed will be populated with an empty value (an empty array,
       * or undefined, whichever is valid for the type of the value.)
       */
      export const build = (partial?: Partial<IShipmentData>): IShipmentData =>
        ({
          ...(partial ?? {}),
        } as IShipmentData);
    }

    export interface IShipmentData {
      token: string | undefined;
      tracking_code: string | undefined;
      in_transit_at: number | undefined;
      delivered_at: number | undefined;
      failed_at: number | undefined;
      tracking_url: string | undefined;
      carrier_name: string | undefined;
      /**
       * Estimated inTransitAt date
       */
      estimated_ship_date: number | undefined;
      /**
       * Estimated deliveredAt date
       */
      estimated_delivery_date: number | undefined;
      status:
        | IListOrdersByBrandOrderResponse.IBrandOrderData.IShipmentData.IShipmentStatus
        | undefined;
      incoterm: string | undefined;
    }

    /**
     * Return an instance of IBrandOrderData with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (
      partial?: Partial<IBrandOrderData>
    ): IBrandOrderData =>
      ({
        items: [],
        shipment_data: [],
        ...(partial ?? {}),
      } as IBrandOrderData);
  }

  /**
   * We break down orders into individual shipments, so each BrandOrderData
   * represents a concrete bundle that has a single ship date. In more backend
   * terms, this model is 1:1 with a BrandOrder
   */
  export interface IBrandOrderData {
    /**
     * Brand order token
     */
    token: string | undefined;
    /**
     * Order token
     */
    order_token: string | undefined;
    /**
     * When this brand order was created
     */
    created_at: number | undefined;
    brand_data:
      | IListOrdersByBrandOrderResponse.IBrandOrderData.IBrandData
      | undefined;
    items: Array<IListOrdersByBrandOrderResponse.IBrandOrderData.IItemData>;
    /**
     * State of the brand order. I.e. NEW, CANCELED, BRAND_CONTACTED, etc etc etc.
     */
    state: keyof typeof IBrandOrder.State | undefined;
    shipped_at: number | undefined;
    delivered_at: number | undefined;
    backordered_at: number | undefined;
    canceled_at: number | undefined;
    brand_review_data:
      | IListOrdersByBrandOrderResponse.IBrandOrderData.IBrandReviewData
      | undefined;
    is_review_editable: boolean | undefined;
    pending_brand_review: boolean | undefined;
    expected_ship_window: IShipWindow | undefined;
    return_eligible: boolean | undefined;
    return_extension_eligible: boolean | undefined;
    restock_eligible: boolean | undefined;
    report_issue_eligibility:
      | keyof typeof IListOrdersByBrandOrderResponse.IBrandOrderData.IEligibility.Enum
      | undefined;
    cancellation_eligibility:
      | keyof typeof IListOrdersByBrandOrderResponse.IBrandOrderData.IEligibility.Enum
      | undefined;
    /**
     * The min "returnableUntil" from order items
     */
    returns_eligible_until: number | undefined;
    creation_reason: keyof typeof IBrandOrder.CreationReason | undefined;
    /**
     * Indicates whether the retailer is awaiting the response of a requested
     * cancellation for this brand order
     */
    has_active_cancellation_request: boolean | undefined;
    /**
     * For international orders, the conversion rate to the retailer's currency when the payment was made.
     */
    payment_conversion_rate: ICurrencyConversionRate | undefined;
    total_price: IFaireMoney | undefined;
    payment_term: keyof typeof PaymentTerm | undefined;
    is_sample_order: boolean | undefined;
    shipping_address: IAddress | undefined;
    preordered: boolean | undefined;
    can_request_duties_compensation_refund: boolean | undefined;
    /**
     * TODO(noah): revisit if we need these
     */
    is_purchase_order: boolean | undefined;
    purchase_order_token: string | undefined;
    retailer_packing_slip_notes: string | undefined;
    shipment_data: Array<IListOrdersByBrandOrderResponse.IBrandOrderData.IShipmentData>;
    /**
     * Status of the brand order with the shipped/delivered/canceled/etc. date, i.e. `Delivered Jan 1, 2016`
     */
    status_string: string | undefined;
    retailer_store_data: IRetailerStoreDataForOrders | undefined;
    /**
     * The applied return window extension fee. This can be null if return wasn't extended.
     */
    applied_return_window_extension_fee: IFaireMoney | undefined;
    /**
     * The expected return window extension fee. This can be null if return was extended.
     * Please check `returnExtensionEligible` before using this value.
     */
    expected_return_window_extension_fee: IFaireMoney | undefined;
  }

  /**
   * Return an instance of IListOrdersByBrandOrderResponse with the provided fields. Any field not
   * listed will be populated with an empty value (an empty array,
   * or undefined, whichever is valid for the type of the value.)
   */
  export const build = (
    partial?: Partial<IListOrdersByBrandOrderResponse>
  ): IListOrdersByBrandOrderResponse =>
    ({
      filter_sections: [],
      orders: [],
      ...(partial ?? {}),
    } as IListOrdersByBrandOrderResponse);
}

/**
 * This message will replace ListOrdersResponse. See this prd for more details:
 * https:www.notion.so/faire/Order-Auditing-2359f32b32464f8a8eba6af4ba0ff3e6
 */
export interface IListOrdersByBrandOrderResponse {
  /**
   * Unique ID for the request. Must be supplied to /click-track endpoint
   * whenever a result from this response is clicked by the user
   */
  request_id: string | undefined;
  /**
   * 1-based pagination data
   */
  pagination_data: IPaginationData | undefined;
  /**
   * FilterSections moved to InvoicesSearchFiltersResponse returned by dedicated list-by-brand-order/filters endpoint
   * @deprecated
   */
  filter_sections: Array<IFilterSection>;
  /**
   * True if the retailer has a currently pending identity verification request
   * This is being replaced by retailerIdvStatus below
   * @deprecated
   */
  has_pending_idv: boolean | undefined;
  /**
   * enum field that represents current IDV status of the retailer
   */
  retailer_idv_status:
    | keyof typeof IListOrdersByBrandOrderResponse.RetailerIdvStatus
    | undefined;
  orders: Array<IListOrdersByBrandOrderResponse.IBrandOrderData>;
}
